import {Button, ButtonGroup, Dialog} from 'platform/components';
import {Space, Text} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useEffect, useState} from 'react';
import {BlockerFunction, Location, useBlocker} from 'react-router-dom';

import {equals, not} from 'ramda';

import i18n from '@dms/i18n';

/**
 * Lightbox galleries encode their state in `lightbox*` query params on the current route.
 * Opening or closing a gallery is not leaving the page, so it must never pop the dialog.
 * Mirrors the platform `Form` unsaved-changes blocker behavior.
 */
const LIGHTBOX_QUERY_PARAM_PREFIX = 'lightbox';

const omitLightboxQueryParams = (search: string) => {
  const params = Array.from(new URLSearchParams(search));

  return new URLSearchParams(
    params.filter(([key]) => !key.startsWith(LIGHTBOX_QUERY_PARAM_PREFIX))
  ).toString();
};

const isLightboxNavigation = (currentLocation: Location, nextLocation: Location) =>
  equals(currentLocation.pathname, nextLocation.pathname) &&
  equals(
    omitLightboxQueryParams(currentLocation.search),
    omitLightboxQueryParams(nextLocation.search)
  );

interface PendingChangesBlockerProps {
  isDirty: boolean;
  /**
   * A save started outside this dialog (a panel's own save button). Drives the dialog
   * affordances only — suppressing the block itself is {@link shouldBlockNavigation}'s job.
   */
  isSaving: boolean;
  /**
   * Consumer-side veto evaluated on every navigation attempt. Return `false` to let the
   * navigation through even though the changes are still dirty.
   *
   * Keep the function identity stable — it re-registers the router blocker on change.
   */
  shouldBlockNavigation: () => boolean;
  /** Resolves `true` when everything saved; `false` keeps the user on the page. */
  onSave: () => Promise<boolean>;
}

/**
 * Guards the vehicle Financing & Leasing editors and the new-vehicle catalogue's equipment-by-trim
 * matrix against losing unsaved changes on navigation.
 *
 * Kept out of the shared `Form` by review decision: both track dirtiness outside a platform
 * `Form`, and the shared `Form` blocker must stay untouched to avoid app-wide regressions. The
 * dialog copy and test ids intentionally match the platform blocker so the page behaves
 * consistently with every form in the app.
 */
export function PendingChangesBlocker(props: PendingChangesBlockerProps) {
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const isSaving = props.isSaving || isSavingChanges;

  // Read the callback off `props` first: calling `props.shouldBlockNavigation()` inside the memo
  // makes the React Compiler treat the whole `props` object as the dependency.
  const shouldBlockNavigation = props.shouldBlockNavigation;

  // react-router's `useBlocker` re-registers the blocker whenever the predicate identity changes.
  const shouldBlock = useCallback<BlockerFunction>(
    ({currentLocation, nextLocation}) =>
      props.isDirty &&
      shouldBlockNavigation() &&
      // A navigation triggered while this dialog's own save runs must proceed, otherwise
      // react-router reports an invalid blocker state transition.
      not(isSavingChanges) &&
      not(isLightboxNavigation(currentLocation, nextLocation)) &&
      not(equals(currentLocation, nextLocation)),
    [props.isDirty, shouldBlockNavigation, isSavingChanges]
  );

  const blocker = useBlocker(shouldBlock);

  // Raw `useEffect` as the documented last resort (`avoiding-use-effect`): this synchronizes
  // react-router's blocker — an external system — with state the blocker predicate cannot
  // re-evaluate on its own. It preserves the platform `Form` blocker semantics: when the
  // changes stop being dirty while a navigation is blocked (the panels saved or discarded
  // through their own controls), the stale dialog closes and the held navigation is cancelled.
  useEffect(
    function resetBlockerWhenClean() {
      if (blocker.state === 'blocked' && not(props.isDirty) && not(isSaving)) {
        blocker.reset?.();
      }
    },
    [blocker, props.isDirty, isSaving]
  );

  const handleSaveAndContinue = async () => {
    setIsSavingChanges(true);

    try {
      const isSuccessful = await props.onSave();

      match(isSuccessful)
        .with(true, () => blocker.proceed?.())
        // Validation failed or the API call reported a failure. Close the dialog and let the
        // user fix the editors — the save pipeline has already surfaced the error to them.
        .otherwise(() => blocker.reset?.());
    } catch (error) {
      // Close the dialog so the user is not trapped behind a modal, then rethrow: a crashed
      // save must never be silent.
      blocker.reset?.();

      throw error;
    } finally {
      setIsSavingChanges(false);
    }
  };

  return (
    <Dialog
      isOpen={blocker.state === 'blocked'}
      // While a save runs its outcome decides between proceeding and staying; closing would reset
      // the held navigation underneath it.
      onClose={() => (isSaving ? undefined : blocker.reset?.())}
      hideCloseButton={isSaving}
      size="small"
      title={i18n.t('general.notifications.unsavedChanges.title')}
      data-testid="formSaver-dialog"
    >
      <Text size="small">{i18n.t('general.notifications.unsavedChanges.description')}</Text>

      <Space vertical={4} />

      <ButtonGroup align="right">
        <Button
          isDisabled={isSaving}
          variant="secondary"
          title={i18n.t('general.actions.leave')}
          onClick={() => blocker.proceed?.()}
          data-testid="formSaverModal-button-goBack"
        />
        <Button
          isLoading={isSaving}
          isDisabled={isSaving}
          title={i18n.t('general.actions.saveAndContinue')}
          onClick={handleSaveAndContinue}
          data-testid="formSaverModal-button-saveAndContinue"
        />
      </ButtonGroup>
    </Dialog>
  );
}
